import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { ConfirmDialog, Pagination } from '../../components';
import { staffActivitiesService, staffPartnersService, STAFF_PAGE_SIZE } from '../../lib/api-service';
import type { PartnerDetail } from '../../lib/types';
import type { StaffActivity } from '../../lib/types';
import { formatDate, formatTime } from '../../utils/date';
import { getMonthName } from '../../utils/date';
import { apiFetch } from '../../lib/api';
import type { PaginatedResponse } from '../../lib/types';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Search,
  Download,
  List,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Users,
  HandHelping,
  Mail,
} from 'lucide-react';
import { HelpButton } from '../../components/HelpButton';

export default function ManageActivities() {
  const navigate = useNavigate();
  const [activities, setActivities] = useState<StaffActivity[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft'>('all');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [allPartners, setAllPartners] = useState<PartnerDetail[]>([]);
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [calendarActivities, setCalendarActivities] = useState<StaffActivity[]>([]);
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<StaffActivity | null>(null);

  useEffect(() => {
    staffPartnersService.list({ page: 1 }).then((data) => setAllPartners(data.results));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page };
      if (searchQuery) params.search = searchQuery;
      if (statusFilter === 'published') params.is_published = 'true';
      if (statusFilter === 'draft') params.is_published = 'false';
      if (partnerFilter) params.partner = partnerFilter;
      const data = await staffActivitiesService.list(params as { page?: number; search?: string });
      setActivities(data.results);
      setTotalCount(data.count);
    } catch {
      setError('Nu s-au putut încărca activitățile.');
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, statusFilter, partnerFilter]);

  useEffect(() => { load(); }, [load]);

  // Reset to page 1 when search/filter changes
  useEffect(() => { setPage(1); }, [searchQuery, statusFilter, partnerFilter]);

  // Load all activities for calendar view
  useEffect(() => {
    if (viewMode === 'calendar') {
      apiFetch<PaginatedResponse<StaffActivity>>('/staff/activities/?page_size=10000')
        .then((data) => setCalendarActivities(data.results))
        .catch(() => {});
    }
  }, [viewMode]);

  async function confirmDelete() {
    if (!deleteTarget) return;
    try {
      await staffActivitiesService.delete(deleteTarget.id);
      setDeleteTarget(null);
      await load();
    } catch {
      setError('Eroare la ștergere.');
    }
  }

  async function togglePublished(a: StaffActivity) {
    try {
      await staffActivitiesService.update(a.id, { is_published: !a.is_published });
      await load();
    } catch {
      setError('Eroare la actualizare.');
    }
  }

  async function exportCSV() {
    const allData = await apiFetch<PaginatedResponse<StaffActivity>>('/staff/activities/?page_size=10000');
    const all = allData.results;
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const headers = [
      'Titlu', 'Slug', 'Data start', 'Data final', 'Locație', 'Descriere',
      'Tag-uri', 'Prieteni', 'Limită participanți', 'Vârstă min', 'Vârstă max',
      'Minori necesită adult', 'Necesită înregistrare', 'Deadline înregistrare',
      'Link înregistrare', 'Publicat', 'Creat la', 'Actualizat la',
    ];
    const rows = all.map((a) => [
      escape(a.title),
      escape(a.slug),
      escape(formatDate(a.start_datetime) + ' ' + formatTime(a.start_datetime)),
      a.end_datetime ? escape(formatDate(a.end_datetime) + ' ' + formatTime(a.end_datetime)) : '',
      escape(a.location_text),
      escape(a.description),
      escape(a.tags.map((t) => t.name).join(', ')),
      escape(a.partners.map((p) => p.name).join(', ')),
      a.participant_limit ?? '',
      a.min_age ?? '',
      a.max_age ?? '',
      a.minors_require_adult ? 'Da' : 'Nu',
      a.requires_registration ? 'Da' : 'Nu',
      a.registration_deadline ? escape(formatDate(a.registration_deadline)) : '',
      a.registration_link ? escape(a.registration_link) : '',
      a.is_published ? 'Da' : 'Nu',
      escape(formatDate(a.created_at)),
      escape(formatDate(a.updated_at)),
    ]);
    const csv = '\uFEFF' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const today = formatDate(new Date().toISOString()).replace(/\//g, '-');
    link.href = blobUrl;
    link.download = `activitati_export_${today}.csv`;
    link.click();
    URL.revokeObjectURL(blobUrl);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--vivid-purple)]" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
            Activități
          </h1>
          <HelpButton to="/staff/documentatie/activitati" />
        </div>
        <div className="flex gap-2">
          <button
            onClick={exportCSV}
            className="flex items-center gap-2 rounded-lg border border-[var(--vivid-purple)] px-4 py-2.5 text-sm font-semibold text-[var(--vivid-purple)] transition-colors hover:bg-[var(--vivid-purple)]/5 disabled:opacity-40"
            style={{ fontFamily: 'var(--font-primary)' }}
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Export CSV</span>
          </button>
          <button
            onClick={() => navigate('/staff/activitati/nou')}
            className="flex items-center gap-2 rounded-lg bg-[var(--vivid-purple)] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--vivid-purple)]/90"
            style={{ fontFamily: 'var(--font-primary)' }}
          >
            <Plus className="h-4 w-4" />
            Activitate Nouă
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {/* View toggle */}
      <div className="mb-4 flex gap-1 rounded-lg bg-gray-100 p-1 w-fit">
        <button
          onClick={() => setViewMode('list')}
          className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground'}`}
        >
          <List className="h-3.5 w-3.5" /> Listă
        </button>
        <button
          onClick={() => setViewMode('calendar')}
          className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'calendar' ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground'}`}
        >
          <CalendarDays className="h-3.5 w-3.5" /> Calendar
        </button>
      </div>

      {viewMode === 'list' ? (
      <>
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            placeholder="Caută activități..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border py-2.5 pl-10 pr-4 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
            style={{ fontFamily: 'var(--font-secondary)' }}
          />
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex gap-1.5">
          {([
            { key: 'all' as const, label: 'Toate' },
            { key: 'published' as const, label: 'Publicate' },
            { key: 'draft' as const, label: 'Draft' },
          ]).map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`rounded-lg px-3 py-2 text-xs font-medium transition-colors ${
                statusFilter === f.key
                  ? 'bg-[var(--vivid-purple)] text-white'
                  : 'bg-white text-muted-foreground hover:bg-gray-50'
              }`}
              style={{ fontFamily: 'var(--font-primary)' }}
            >
              {f.label}
            </button>
          ))}
        </div>
        <select
          value={partnerFilter}
          onChange={(e) => setPartnerFilter(e.target.value)}
          className="rounded-lg border bg-white px-3 py-2 text-xs font-medium text-muted-foreground outline-none transition-colors focus:border-[var(--vivid-purple)]"
          style={{ fontFamily: 'var(--font-primary)' }}
        >
          <option value="">Toți prietenii</option>
          {allPartners.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      <div className="overflow-hidden rounded-xl border bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50/50">
              <th className="px-4 py-3 text-left font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>Titlu</th>
              <th className="hidden px-4 py-3 text-left font-semibold sm:table-cell" style={{ fontFamily: 'var(--font-primary)' }}>Data</th>
              <th className="hidden px-4 py-3 text-left font-semibold md:table-cell" style={{ fontFamily: 'var(--font-primary)' }}>Tag-uri</th>
              <th className="hidden px-4 py-3 text-left font-semibold lg:table-cell" style={{ fontFamily: 'var(--font-primary)' }}>Înscriși</th>
              <th className="px-4 py-3 text-left font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>Status</th>
              <th className="px-4 py-3 text-right font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>Acțiuni</th>
            </tr>
          </thead>
          <tbody>
            {activities.map((a) => {
              const dateStr = formatDate(a.start_datetime);
              const timeStr = formatTime(a.start_datetime);
              return (
                <tr key={a.id} className="border-b last:border-b-0 hover:bg-gray-50/50">
                  <td className="px-4 py-3">
                    <div className="font-medium" style={{ fontFamily: 'var(--font-primary)' }}>{a.title}</div>
                  </td>
                  <td className="hidden px-4 py-3 sm:table-cell">
                    <span className="text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                      {dateStr}, {timeStr}
                    </span>
                  </td>
                  <td className="hidden px-4 py-3 md:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {a.tags.slice(0, 3).map((t) => (
                        <span
                          key={t.id}
                          className="rounded-full px-2 py-0.5 text-xs font-medium"
                          style={{
                            backgroundColor: t.color ? `${t.color}20` : '#f0f0f0',
                            color: t.color || '#666',
                          }}
                        >
                          {t.name}
                        </span>
                      ))}
                      {a.tags.length > 3 && (
                        <span className="text-xs text-muted-foreground">+{a.tags.length - 3}</span>
                      )}
                    </div>
                  </td>
                  <td className="hidden px-4 py-3 lg:table-cell">
                    {a.requires_registration && a.registration_type !== 'external' ? (
                      <span className="text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                        {a.registration_count}{a.participant_limit ? `/${a.participant_limit}` : ''}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground/50">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <label className="relative inline-flex cursor-pointer items-center gap-2" title={a.is_published ? 'Publicat — click pentru dezpublicare' : 'Draft — click pentru publicare'}>
                      <input
                        type="checkbox"
                        checked={a.is_published}
                        onChange={() => togglePublished(a)}
                        className="peer sr-only"
                      />
                      <div className="h-5 w-9 rounded-full bg-gray-300 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:bg-green-500 peer-checked:after:translate-x-full" />
                      <span className={`text-xs font-medium ${a.is_published ? 'text-green-700' : a.is_volunteer_visible ? 'text-[var(--vivid-purple)]' : 'text-muted-foreground'}`}>
                        {a.is_published ? 'Publicat' : a.is_volunteer_visible ? 'Voluntari' : 'Draft'}
                      </span>
                    </label>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      {a.requires_registration && a.registration_type !== 'external' && (
                        <button
                          onClick={() => navigate(`/staff/activitati/${a.slug}/participanti`)}
                          className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-gray-100 hover:text-[var(--vivid-purple)]"
                          title="Participanți"
                        >
                          <Users className="h-4 w-4" />
                        </button>
                      )}
                      {a.volunteer_applications_open && (
                        <button
                          onClick={() => navigate(`/staff/activitati/${a.slug}/voluntari`)}
                          className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-gray-100 hover:text-[var(--vivid-purple)]"
                          title="Voluntari"
                        >
                          <HandHelping className="h-4 w-4" />
                        </button>
                      )}
                      {a.is_invitation_only && (
                        <button
                          onClick={() => navigate(`/staff/activitati/${a.slug}/invitatii`)}
                          className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-gray-100 hover:text-[var(--vivid-purple)]"
                          title="Invitații"
                        >
                          <Mail className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/staff/activitati/${a.id}`)}
                        className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-gray-100 hover:text-[var(--vivid-purple)]"
                        title="Editează"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeleteTarget(a)}
                        className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600"
                        title="Șterge"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {activities.length === 0 && (
          <div className="flex flex-col items-center py-12 text-center">
            <p className="text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
              Nu am găsit activități
            </p>
          </div>
        )}
      </div>
      <Pagination page={page} totalCount={totalCount} pageSize={STAFF_PAGE_SIZE} onPageChange={setPage} />
      </>
      ) : (
      /* Calendar View */
      <StaffCalendarView
        activities={calendarActivities}
        year={calYear}
        month={calMonth}
        selectedDay={selectedDay}
        onPrevMonth={() => { if (calMonth === 0) { setCalYear(calYear - 1); setCalMonth(11); } else setCalMonth(calMonth - 1); setSelectedDay(null); }}
        onNextMonth={() => { if (calMonth === 11) { setCalYear(calYear + 1); setCalMonth(0); } else setCalMonth(calMonth + 1); setSelectedDay(null); }}
        onSelectDay={setSelectedDay}
        onEditActivity={(id) => navigate(`/staff/activitati/${id}`)}
      />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Șterge activitatea"
        description={`Sigur vrei să ștergi „${deleteTarget?.title}"? Acțiunea este ireversibilă.`}
      />
    </div>
  );
}

// --- Staff Calendar View ---

const DAY_HEADERS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

function StaffCalendarView({ activities, year, month, selectedDay, onPrevMonth, onNextMonth, onSelectDay, onEditActivity }: {
  activities: StaffActivity[];
  year: number;
  month: number;
  selectedDay: string | null;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onSelectDay: (day: string | null) => void;
  onEditActivity: (id: number) => void;
}) {
  // Group activities by date
  const byDate: Record<string, StaffActivity[]> = {};
  for (const a of activities) {
    const key = a.start_datetime.slice(0, 10);
    if (!byDate[key]) byDate[key] = [];
    byDate[key].push(a);
  }

  // Build grid
  const firstDow = new Date(year, month, 1).getDay();
  const offset = firstDow === 0 ? 6 : firstDow - 1;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev = new Date(year, month, 0).getDate();

  const cells: { day: number; current: boolean; key: string }[] = [];
  for (let i = offset - 1; i >= 0; i--) {
    const d = daysInPrev - i;
    const m = month === 0 ? 12 : month;
    const y = month === 0 ? year - 1 : year;
    cells.push({ day: d, current: false, key: `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}` });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, current: true, key: `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}` });
  }
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    const m = month === 11 ? 1 : month + 2;
    const y = month === 11 ? year + 1 : year;
    cells.push({ day: d, current: false, key: `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}` });
  }

  const todayKey = (() => { const t = new Date(); return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`; })();
  const dayActivities = selectedDay ? (byDate[selectedDay] || []) : [];

  return (
    <div>
      {/* Month nav */}
      <div className="mb-4 flex items-center justify-between">
        <button onClick={onPrevMonth} className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-gray-100">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--font-primary)', color: 'var(--vivid-purple)' }}>
          {getMonthName(month)} {year}
        </h2>
        <button onClick={onNextMonth} className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-gray-100">
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Grid */}
      <div className="overflow-hidden rounded-xl border bg-white shadow-sm">
        <div className="grid grid-cols-7 border-b bg-gray-50/50">
          {DAY_HEADERS.map((name, i) => (
            <div key={i} className="py-2 text-center text-xs font-bold text-[var(--vivid-purple)]">{name}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((cell, i) => {
            const events = cell.current ? (byDate[cell.key] || []) : [];
            const isToday = cell.key === todayKey;
            const isSelected = cell.key === selectedDay;
            const pubCount = events.filter((e) => e.is_published).length;
            const draftCount = events.filter((e) => !e.is_published).length;

            return (
              <button
                key={i}
                onClick={() => cell.current && onSelectDay(cell.key === selectedDay ? null : cell.key)}
                disabled={!cell.current}
                className={`relative flex flex-col items-center gap-1 border-b border-r py-3 transition-colors ${
                  !cell.current ? 'bg-gray-50/50 text-muted-foreground/40' : 'hover:bg-gray-50'
                } ${isSelected ? 'bg-[var(--vivid-purple)]/5' : ''}`}
                style={{ borderColor: 'rgba(0,0,0,0.05)', minHeight: 56 }}
              >
                <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${
                  isToday ? 'bg-[var(--vivid-purple)] text-white' : isSelected ? 'bg-[var(--vivid-purple)]/10 text-[var(--vivid-purple)]' : ''
                }`}>
                  {cell.day}
                </span>
                {events.length > 0 && (
                  <div className="flex gap-0.5">
                    {pubCount > 0 && <span className="h-1.5 w-1.5 rounded-full bg-green-500" />}
                    {draftCount > 0 && <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />}
                    {events.length > 2 && <span className="text-[8px] font-bold text-muted-foreground">{events.length}</span>}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Day detail */}
      {selectedDay && (
        <div className="mt-4 rounded-xl border bg-white p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
            {formatDate(selectedDay + 'T00:00:00')}
            <span className="ml-2 text-xs font-normal text-muted-foreground">{dayActivities.length} {dayActivities.length === 1 ? 'activitate' : 'activități'}</span>
          </h3>
          {dayActivities.length === 0 ? (
            <p className="py-4 text-center text-xs text-muted-foreground">Nicio activitate în această zi.</p>
          ) : (
            <div className="space-y-2">
              {dayActivities.sort((a, b) => a.start_datetime.localeCompare(b.start_datetime)).map((a) => (
                <button
                  key={a.id}
                  onClick={() => onEditActivity(a.id)}
                  className={`flex w-full items-center gap-3 rounded-lg p-3 text-left transition-all hover:bg-gray-50 active:scale-[0.98] ${
                    a.is_published ? 'border border-gray-200' : 'border border-dashed border-amber-300 bg-amber-50/30'
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>{a.title}</span>
                      {a.is_published ? (
                        <Eye className="h-3 w-3 text-green-600" />
                      ) : (
                        <EyeOff className="h-3 w-3 text-amber-500" />
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                      {formatTime(a.start_datetime)}
                      {a.end_datetime && ` — ${formatTime(a.end_datetime)}`}
                      {a.location_text && ` · ${a.location_text}`}
                    </span>
                  </div>
                  <Pencil className="h-4 w-4 text-muted-foreground" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="mt-3 flex items-center gap-4 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-green-500" /> Publicat</span>
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-400" /> Draft</span>
      </div>
    </div>
  );
}
